'use client';

import { useCallback } from 'react';
import { useStore } from '@/app/providers/store';
import type { TaskSettings, ToolTaskType } from '@/app/types';
import { useToolFormValidation } from './use-tool-form-validation';

type ToolFormSettings = Partial<TaskSettings> & {
  resolution?: string;
};

interface UseToolFormResult {
  settings: ToolFormSettings;
  updateSettings: (newSettings: Partial<ToolFormSettings>) => void;
  formValidationErrorMessage: string;
}

const DEFAULT_UPSCALE_SETTINGS: ToolFormSettings = {
  resolution: '2k',
};

const EMPTY_TOOL_SETTINGS: ToolFormSettings = {};

export const useToolForm = (type: ToolTaskType): UseToolFormResult => {
  const formValidationErrorMessage = useToolFormValidation({ taskType: type });
  const { formParams, updateFormParams } = useStore((state) => {
    switch (type) {
      case 'face_swap':
        return {
          formParams: state.faceSwapFormParams,
          updateFormParams: state.updateFaceSwapFormParams,
        };
      case 'remake':
        return {
          formParams: state.remakeFormParams,
          updateFormParams: state.updateRemakeFormParams,
        };
      case 'one_click':
        return {
          formParams: state.oneClickVideoProductionFormParams,
          updateFormParams: state.updateOneClickVideoProductionFormParams,
        };
      case 'music_video':
        return {
          formParams: state.oneClickMvFormParams,
          updateFormParams: state.updateOneClickMvFormParams,
        };
      case 'lip_sync':
        return {
          formParams: state.lipSyncFormParams,
          updateFormParams: state.updateLipSyncFormParams,
        };
      case 'upscale':
        return {
          formParams: state.upscaleVideoFormParams,
          updateFormParams: state.updateUpscaleVideoFormParams,
        };
      case 'upscale_image':
        return {
          formParams: state.upscaleImageFormParams,
          updateFormParams: state.updateUpscaleImageFormParams,
        };
      case 'tts':
        return {
          formParams: state.text2ttsFormParams,
          updateFormParams: state.updateText2TtsFormParams,
        };
      case 'style_transfer':
        return {
          formParams: state.styleTransferFormParams,
          updateFormParams: state.updateStyleTransferFormParams,
        };
      case 'motion_control':
        return {
          formParams: state.motionControlFormParams,
          updateFormParams: state.updateMotionControlFormParams,
        };
      case 'character_creation':
        return {
          formParams: state.characterCreationFormParams,
          updateFormParams: state.updateCharacterCreationFormParams,
        };
    }
  });

  const settings = (formParams.settings ||
    (type === 'upscale' || type === 'upscale_image'
      ? DEFAULT_UPSCALE_SETTINGS
      : EMPTY_TOOL_SETTINGS)) as ToolFormSettings;

  const updateSettings = useCallback(
    (newSettings: Partial<ToolFormSettings>) => {
      updateFormParams('settings', { ...settings, ...newSettings });
    },
    [settings, updateFormParams],
  );

  return {
    settings,
    updateSettings,
    formValidationErrorMessage,
  };
};
